/**
 * AI Assistant API Schemas (#765)
 *
 * Pure Zod schemas for the assistant contract: ask request, thread listing query,
 * the model's structured output, and the answer/thread DTOs the client validates
 * with `validatedRequest`. Spec: docs/plans/2026-09-11-assistant.md §2.1.
 *
 * SINGLE SOURCE OF TRUTH for the assistant API contract.
 */

import { z } from 'zod/v4';
import { crmEntityTypeSchema } from './records';
import { fitsSearchTerm } from './search';

// ============================================================================
// Enums
// ============================================================================

/**
 * Contour the question is asked in. All three are accepted by the schema from
 * day one; in Ф1 only `record` is served — the route answers 422
 * `scope_not_available` for the others (§1, §2.2).
 */
export const assistantScopeSchema = z.enum(['record', 'entity', 'org']);

/**
 * Contours served today. The schema accepts all three from day one; a scope
 * outside this list is answered 422 `scope_not_available` — by edge-api
 * BEFORE any row is touched, by workflows-api before any work (§2.2), and the
 * UI does not offer it.
 *
 * `entity` was released in PR 5b (#765), one change with its legal
 * disclosure, the same order as the record contour (PR 3b): a contour here
 * that `privacy.astro`, `dpa.astro` and `sub-processors.astro` do not describe
 * is undocumented processing, and a missing UI does not help — the API is
 * reachable with an ordinary token. What that release required:
 *
 * 1. the viewer's row boundary reaches the feed — edge-api computes it and
 *    forwards it with the turn, `listByCrmEntity` applies it in SQL (PR 4);
 * 2. the legal surfaces describe the flow (PR 5b);
 * 3. deleting a record deletes the entity turns built from it (#988): a turn
 *    writes its sources before reading any content, and a trigger on
 *    Postgres `records` deletes every turn whose sources overlap the deleted
 *    rows. A record that never reached the Postgres mirror escaped the
 *    trigger — accepted for the release (such a record enters the prompt as
 *    its date and type only) and closed by #999: a scheduled sweep walks
 *    the turns and deletes those with a source gone from D1
 *    (`runAssistantSourceSweep` in workflows-api);
 * 4. the panel reads an entity's history (#962).
 *
 * `org` was released in PR 7b (#765 Ф3), the same way: with the disclosure of
 * the whole-base flow in the three legal documents. What it required beyond
 * the entity contour: the viewer's boundary applied to candidates and to the
 * index query (PR 6), the index rebuilt in full on the environment before the
 * release reaches it (the org job reads only what the index holds), and the
 * `/assistant` page with its filters (PR 7a).
 *
 * The list lives here, not in either worker: every side gates on it, and a
 * list with two homes would release a contour on one side only.
 */
export const ASSISTANT_RELEASED_SCOPES = [
  'record',
  'entity',
  'org',
] as const satisfies readonly AssistantScope[];

/**
 * A plain `boolean`, deliberately not a `scope is …` type guard. The list is
 * release policy, not a fact about the type: as a guard, removing `entity`
 * from it narrowed every checked scope to `'record'`, and the compiler
 * declared the whole built-but-unreleased entity branch unreachable
 * (TS2367) — and `org` sat in that position until PR 7b.
 */
export function isAssistantScopeReleased(scope: string): scope is AssistantScope {
  return (ASSISTANT_RELEASED_SCOPES as readonly string[]).includes(scope);
}

/** Lifecycle of one turn in `assistant_turns` (§6.1). */
export const assistantTurnStatusSchema = z.enum([
  'pending',
  'running',
  'completed',
  'failed',
]);

/**
 * Turn-level `error_code` values (§2.2, second table). With `status:'failed'` —
 * `stale_timeout`, `kill_switch`, `answer_invalid`, `llm_unavailable`,
 * `search_unavailable`, `record_deleted`; with `status:'completed'` —
 * `access_revoked` (answer suppressed at read time: a cited record is no longer
 * visible to the viewer, §3.6 п.6). `access_revoked` with `status:'failed'` —
 * a «whole base» job stopped before reading: the asker's role changed while
 * it waited in the queue (#765 Ф3). `transcript_unredactable` with
 * `status:'failed'` — every record the «whole base» search found was refused
 * by the PII redaction (#2427): final, the same texts refuse the same way.
 * `records_partly_unreadable` with `status:'failed'` — the same, but the rest of
 * the found records have no analysis or text yet (#2478): the refused ones are
 * final, the rest can be brought to a state the next question reads.
 */
export const assistantErrorCodeSchema = z.enum([
  'stale_timeout',
  'kill_switch',
  'answer_invalid',
  'llm_unavailable',
  'search_unavailable',
  'transcript_unredactable',
  'records_partly_unreadable',
  'record_deleted',
  'access_revoked',
]);

/** Outcome of locating a citation quote in the diarization (§3.6). */
export const assistantGroundingSchema = z.enum(['grounded', 'ambiguous', 'ungrounded']);

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * The name fits the filter: the request schema accepts it and the search
 * matches it WHOLE. Counted as the search counts (`fitsSearchTerm`) — a name
 * that fits only by a looser count would be cut to a prefix there and match
 * every customer sharing it (Codex P2, #1312).
 */
export function fitsAssistantEntityName(name: string): boolean {
  return fitsSearchTerm(name);
}

/**
 * Structural filters for the `org` contour. Strict: an unknown filter key would
 * silently be ignored server-side and the user would believe the answer was
 * narrowed when it was not.
 */
export const assistantOrgFiltersSchema = z
  .object({
    // `{ offset: true }`: the browser may send a local-zone offset (`+03:00`), and
    // bare `.datetime()` accepts only `Z` — analytics.ts does the same for ranges.
    dateFrom: z.string().datetime({ offset: true }).optional(),
    dateTo: z.string().datetime({ offset: true }).optional(),
    projectIds: z.array(z.string().uuid()).max(20).optional(),
    // Идентификаторы агентов (`records_metadata.agent_id`) — хранятся как есть,
    // без минимизации свободного текста, поэтому ограничены по форме.
    agentIds: z.array(z.string().min(1).max(128)).max(50).optional(),
    type: z.enum(['call', 'chat']).optional(),
    callCategory: z.string().optional(),
    // Имя из CRM, §4.3 — из автодополнения (`/assistant/entity-names`), не
    // вводом руками. Граница по байтам (`fitsAssistantEntityName`), поэтому
    // `truncateSearchTerm` в этом поле не срабатывает.
    entityName: z
      .string()
      .trim()
      .min(2)
      .refine(fitsAssistantEntityName, 'entity_name_too_long')
      .optional(),
  })
  .strict();
// `outcome` (open|won|lost) в MVP нет: `listByOrgId` фильтра по исходу не имеет
// (`RecordMetadataBaseRepository.ts:860-935`), исход есть только у deal-серий в D1
// `call_series_outcomes` — добавляется вторым шагом веткой `EXISTS (…)` в `buildListConditions`.

/**
 * What the question is about. Discriminated on `scope` so the SSOT itself forbids
 * `filters` outside the `org` variant — no superRefine needed (§2.1).
 */
export const assistantSubjectSchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('record'), recordId: z.string().uuid() }).strict(),
  z
    .object({
      scope: z.literal('entity'),
      entityType: crmEntityTypeSchema, // deal|lead|contact|company (records.ts:39)
      entityId: z.string().min(1).max(255),
      // CRM-id локальны для портала (state.ts:920-925). НЕ uuid: у Uspacy id инсталляции —
      // `uspacy_install_<uuid>` (`routes/organizations/uspacy.ts:468`), колонка `embedded_installs.id text`.
      installationId: z.string().min(1).max(255).nullable(),
    })
    .strict(),
  // filters живут ВНУТРИ варианта org — SSOT сам запрещает их для record/entity,
  // superRefine не нужен. Варианты strict: чужое поле (`filters` у record) не
  // срезается молча — иначе вопрос отвечался бы по всей записи, хотя запрос
  // выглядел отфильтрованным (Codex P2, раунд 29).
  z
    .object({ scope: z.literal('org'), filters: assistantOrgFiltersSchema.optional() })
    .strict(),
]);

/**
 * `POST /assistant/ask` body. Header `Idempotency-Key: <uuid>` is mandatory; a
 * replay with the same key returns the existing turn (§2.2).
 */
/**
 * The question's length cap. Named so the composer can enforce it before the
 * request instead of letting the user discover it as a generic failure
 * (Codex P2, PR 3 round 1).
 */
export const ASSISTANT_QUESTION_MAX_CHARS = 2_000;

/**
 * The wording of `no_transcript`: edge-api refuses a short or missed call
 * with it before the paid pass over the question, workflows-api every other
 * record without a transcript (#2449) — one text for one code.
 */
export const ASSISTANT_NO_TRANSCRIPT_ERROR = 'Record has no transcript yet';

/**
 * A turn still `pending`/`running` this long after its `createdAt` is settled
 * by the server as `failed`/`stale_timeout` — when `GET /turns/:id` reads it
 * and when `GET /threads` lists it. The client re-reads history at that
 * moment instead of keeping a settlement machinery of its own (§3.7).
 */
export const ASSISTANT_STALE_TURN_AFTER_MS = 10 * 60_000;

/**
 * The edge answers `POST /ask` with 503 `llm_unavailable` — the row kept for
 * a later poll — once its proxy to workflows-api has waited this long; the
 * client's own deadline sits a little behind it (§2.2).
 */
export const ASSISTANT_PROXY_DEADLINE_MS = 110_000;

export const assistantAskRequestSchema = z
  .object({
    subject: assistantSubjectSchema,
    question: z.string().trim().min(1).max(ASSISTANT_QUESTION_MAX_CHARS),
    threadId: z.string().uuid().optional(), // продолжить конкретный тред; без него — последний тред пользователя по subject (§3.7)
    newThread: z.boolean().optional(), // явное «новый диалог»
  })
  .strict()
  // `threadId` называет тред, который продолжить; `newThread` просит новый.
  // Оба сразу — противоречие, которое сервер иначе разрешал бы молчаливым
  // приоритетом одного над другим.
  .refine((body) => !(body.threadId !== undefined && body.newThread === true), {
    path: ['newThread'],
    message: 'newThread cannot be combined with threadId',
  });

const THREADS_QUERY_SUBJECT_KEYS = [
  'recordId',
  'entityType',
  'entityId',
  'installationId',
] as const;
type ThreadsQuerySubjectKey = (typeof THREADS_QUERY_SUBJECT_KEYS)[number];

/**
 * Subject keys per scope — the flat-query mirror of `assistantSubjectSchema`'s
 * variants. Every key outside `required`+`optional` is rejected, not ignored:
 * the subject builder reads only the keys of the selected scope, so a `record`
 * query carrying `entityId` would silently list threads of the whole record while
 * the caller believed it narrowed the subject (Codex P2, round 34).
 */
const THREADS_QUERY_SHAPE: Record<
  z.infer<typeof assistantScopeSchema>,
  {
    required: readonly ThreadsQuerySubjectKey[];
    optional: readonly ThreadsQuerySubjectKey[];
  }
> = {
  record: { required: ['recordId'], optional: [] },
  entity: { required: ['entityType', 'entityId'], optional: ['installationId'] },
  org: { required: [], optional: [] },
};

/**
 * Query `GET /assistant/threads`. null в query-string не передать, поэтому отсутствующий
 * `installationId` = null; сервер строит subject_key той же `buildSubjectKey(subject)`,
 * что и POST /ask (§6.1). Which keys are required depends on `scope`, and a flat
 * query string cannot be a discriminated union — hence the superRefine over
 * `THREADS_QUERY_SHAPE`.
 *
 * Not strict, unlike the JSON bodies: a query string is transport. The
 * ui-worker's `authenticatedFetch` keys the browser cache per organization
 * by appending `_org=<id>` to every GET, so a strict schema refused every
 * real history read with 400 (owner's review, 2026-09-17). Unknown keys
 * are stripped; the subject keys the scope forbids are still refused below.
 */
/**
 * The question's other filters, as the suggestions read them: everything but
 * the name, which is what is being chosen.
 */
export const assistantEntityNamesFiltersSchema = assistantOrgFiltersSchema.omit({
  entityName: true,
});

/**
 * `GET /assistant/entity-names?q=&filters=` — the customer-name autocomplete
 * of the `org` filters (§4.3). Not strict, like every query string here (see
 * `assistantThreadsQuerySchema`). Two characters at least: one would list half
 * the organization.
 *
 * `filters` — the question's other filters as JSON: a name is suggested only
 * when at least one record matches it under them, so a picked customer never
 * meets a category or a period that excludes every record of theirs (#1361).
 * Malformed JSON is refused, not ignored: suggestions silently wider than the
 * filters on screen are the very defect this closes.
 */
export const assistantEntityNamesQuerySchema = z.object({
  q: z.string().trim().min(2).max(40),
  filters: z
    .preprocess((raw) => {
      if (typeof raw !== 'string') return raw;
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    }, assistantEntityNamesFiltersSchema)
    .optional(),
});

/** Suggestions per request: a dropdown, not a listing. */
export const ASSISTANT_ENTITY_NAME_SUGGESTIONS = 20;

/** Up to 20 names as stored, each short enough to be a filter. */
export const assistantEntityNamesResponseSchema = z.object({
  data: z.object({ names: z.array(z.string()) }),
});

/**
 * History lists the caller's newest threads on a subject, this many (§2.2).
 * A list that holds exactly this many may have older threads behind it.
 */
export const ASSISTANT_THREADS_PER_SUBJECT = 5;

export const assistantThreadsQuerySchema = z
  .object({
    scope: assistantScopeSchema,
    recordId: z.string().uuid().optional(),
    entityType: crmEntityTypeSchema.optional(),
    entityId: z.string().min(1).max(255).optional(),
    installationId: z.string().min(1).max(255).optional(),
  })
  .superRefine((query, ctx) => {
    const shape = THREADS_QUERY_SHAPE[query.scope];
    for (const key of shape.required) {
      if (query[key] === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: `${key} is required for scope=${query.scope}`,
        });
      }
    }
    for (const key of THREADS_QUERY_SUBJECT_KEYS) {
      if (query[key] === undefined) continue;
      if (shape.required.includes(key) || shape.optional.includes(key)) continue;
      // A stray key would also build a different subject_key than the one
      // POST /ask used — the thread list would come back empty.
      ctx.addIssue({
        code: 'custom',
        path: [key],
        message: `${key} is not allowed for scope=${query.scope}`,
      });
    }
  });

// ============================================================================
// Model output
// ============================================================================

/**
 * Форма вывода МОДЕЛИ — от неё строится strict json_schema и ею делается safeParse (§3.1).
 * Без speaker/startSec/grounding: их ставит сервер после grounding (§3.6); `at` — подсказка-локатор.
 * Пределы здесь — то, что модель ВПРАВЕ выдать; схемы ответа ниже шире (см. комментарий там).
 */
/** Whitespace is not text: a blank thesis or quote goes to repair (Codex P2, round 26). */
const nonBlank = (value: string): boolean => value.trim().length > 0;

export const assistantModelOutputSchema = z
  .object({
    answer: z.string().max(4_000).nullable(),
    notFound: z.boolean(),
    points: z
      .array(
        z.object({
          // «   » passed `.max(400)` and the point was stored with no text.
          // `.min(1)` mirrors the strict schema's `minLength`; the trim check
          // is Zod's alone and sends the output to repair.
          thesis: z.string().min(1).max(400).refine(nonBlank, { message: 'blank' }),
          detail: z.string().max(2_000).nullable(),
          // `.min(1)` — правило 2 §3.2: тезис без цитаты не является ответом модели,
          // такой вывод отвергается safeParse и уходит на repair-попытку. Схема ОТВЕТА
          // (`assistantPointSchema` ниже) оставляет min 0 намеренно: grounding может
          // выбросить чужую/невалидную цитату ПОСЛЕ проверки вывода (§3.6 п.6), и
          // тезис с опустевшим списком должен остаться читаемым для клиента.
          citations: z
            .array(
              z.object({
                recordId: z.string().uuid(),
                quote: z.string().min(1).max(600).refine(nonBlank, { message: 'blank' }),
                // «MM:SS» (минуты 0–59) | «H:MM:SS», как в данных. Часы — до
                // шести цифр: контекст чата тянется неделями, и
                // `timestampFromSeconds` пишет в промпт «[100:00:00]» — модель
                // должна суметь скопировать подсказку, иначе цитата с
                // повторяющимся текстом уходит в `null` или в чужое вхождение
                // (Codex P2, раунд 20); потолок — чтобы случайное число не
                // стало подсказкой далеко за концом транскрипта. Минуты и
                // секунды ограничены 00–59: «99:99» проходило бы форму, а
                // `parseAtSeconds` превращал бы его в подсказку на 100-й минуте.
                at: z
                  .string()
                  .regex(/^(?:[0-5]?\d:[0-5]\d|\d{1,6}:[0-5]\d:[0-5]\d)$/)
                  .nullable(),
              })
            )
            .min(1)
            .max(5),
        })
      )
      .max(12),
    // A blank follow-up is not a suggestion: `[""]` was stored and rendered
    // as an empty action (Codex P2, round 27) — repair, like a blank thesis.
    suggestedQuestions: z
      .array(z.string().min(1).max(200).refine(nonBlank, { message: 'blank' }))
      .max(6),
  })
  .strict()
  // Правило 1 системного промпта: `notFound: true` означает «в данных ответа
  // нет» и требует `points: []`. Строгая JSON-схема провайдера выразить связь
  // двух полей не может, поэтому модель может вернуть «не найдено» и при
  // этом тезисы с цитатами — противоречие, которое клиент показал бы как
  // ответ с пометкой «не найдено». safeParse в `runLlmAttempts` отправляет
  // такой вывод на repair-попытку (structure-only hint: путь + код).
  .superRefine((output, ctx) => {
    if (output.notFound && output.points.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['points'],
        message: 'not_found_with_points',
      });
    }
    // Обратная сторона правил 1–2: вывод без цитаты — не ответ. `notFound:false`
    // с пустым `points` — это заключение, которое нечем подтвердить; клиент
    // показал бы текст «найдено» без единой ссылки на запись. Та же
    // repair-попытка, тот же путь `points`.
    if (!output.notFound && output.points.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['points'],
        message: 'found_without_points',
      });
    }
    // Правило 4 системного промпта: сначала прямой ответ в одно–три
    // предложения, потом тезисы. `notFound:false` с `null` или пустым `answer`
    // — тезисы без вывода: клиент показал бы одни опорные пункты, а прямого
    // ответа пользователь так и не получил бы (Codex P2, раунд 19). При
    // `notFound:true` пустой `answer` допустим: правило 1 просит объяснить,
    // чего не хватает, но состояние «не найдено» читаемо и без текста.
    if (!output.notFound && (output.answer === null || output.answer.trim() === '')) {
      ctx.addIssue({
        code: 'custom',
        path: ['answer'],
        message: 'found_without_answer',
      });
    }
  });

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * Daily ceiling state (§7.3): `remainingToday` — calendar day of the organization;
 * `resetAt` — nearest local midnight, the only source (headers do not survive the proxy).
 */
export const assistantQuotaSchema = z.object({
  remainingToday: z.number().int().nullable(),
  resetAt: z.string().datetime({ offset: true }).nullable(),
});

/**
 * `GET /quota` answer: the ceiling plus the contours stopped by the kill
 * switch (#1059). Only this response carries them — an answer's `quota`
 * does not, so the two shapes are separate. Optional: an edge-api deployed
 * before it sends none, and absence means «nothing stopped». Plain strings,
 * not the scope enum: a contour added later must not make a bundle loaded
 * before it reject the whole quota answer.
 */
export const assistantQuotaResponseSchema = assistantQuotaSchema.extend({
  pausedScopes: z.array(z.string()).optional(),
});

// Пределы ОТВЕТА шире пределов вывода МОДЕЛИ (600/400/2000/4000/200 в
// `assistantModelOutputSchema`) намеренно: перед записью ответ проходит PII-минимизацию (§6.2),
// а её замены бывают ДЛИННЕЕ исходного текста (`address: Kyiv` → `address [ADDRESS]`,
// `a@b.io` → `[EMAIL]`). Цитата в 598 символов после маски становится 602 — с равными
// пределами клиентский `validatedRequest` отверг бы весь ответ, и ход, за который уже
// заплачено и который уже `completed`, стал бы нечитаемым при каждом повторе.
//
// Единственное место, где эти пределы названы: `AssistantTurnRepository.complete()`
// обрезает минимизированный текст по ним же (по границе графемы) перед записью —
// ход, чей текст после маски перерос предел, иначе не проходил бы схему на клиенте.
export const ASSISTANT_RESPONSE_TEXT_CAPS = {
  answer: 5_000,
  thesis: 500,
  detail: 2_500,
  quote: 800,
  suggestedQuestion: 250,
} as const;

export const assistantCitationSchema = z.object({
  recordId: z.string().uuid(),
  // `recordTypeSchema` (records.ts:28) содержит и `email`; сужено намеренно — записей `type:'email'`
  // ингест не создаёт (grep: только delivery-target). На входе фильтровать, а не падать.
  recordType: z.enum(['call', 'chat']),
  quote: z.string().max(ASSISTANT_RESPONSE_TEXT_CAPS.quote), // может быть '' после PII-минимизации при записи (§6.2) — тайм-код остаётся
  speaker: z.enum(['agent', 'client', 'unknown']),
  startSec: z.number().nonnegative().nullable(), // null = ungrounded/ambiguous
  endSec: z.number().nonnegative().nullable(),
  grounding: assistantGroundingSchema,
  recordDeleted: z.boolean(), // запись удалена/невидима при перечитывании треда (§3.6 п.6)
});

export const assistantPointSchema = z.object({
  thesis: z.string().max(ASSISTANT_RESPONSE_TEXT_CAPS.thesis),
  detail: z.string().max(ASSISTANT_RESPONSE_TEXT_CAPS.detail).nullable(),
  // min 0, а не min 1 как у модели: grounding отбрасывает цитаты на чужую запись
  // после safeParse (§3.6 п.6) — иначе сохранённый и оплаченный ход не прошёл бы
  // `validatedRequest` на клиенте.
  citations: z.array(assistantCitationSchema).max(5),
});

/**
 * One turn as the client sees it. The same schema serves `POST /ask`, polling
 * `GET /turns/:id` and thread history: a 202 for `org` returns it with
 * `status:'pending', answer:null, points:[]`, so `validatedRequest` validates
 * every path with one schema (§2.1).
 */
export const assistantAnswerSchema = z.object({
  turnId: z.string().uuid(),
  threadId: z.string().uuid(),
  scope: assistantScopeSchema,
  status: assistantTurnStatusSchema,
  answer: z.string().max(ASSISTANT_RESPONSE_TEXT_CAPS.answer).nullable(), // прямой ответ 1–3 предложения
  points: z.array(assistantPointSchema).max(12),
  notFound: z.boolean(), // «в данных этого нет»
  suggestedQuestions: z
    .array(z.string().max(ASSISTANT_RESPONSE_TEXT_CAPS.suggestedQuestion))
    .max(6),
  sources: z.object({
    recordsConsidered: z.number().int(), // entity: прочитанная лента (до потолка в 2 000 записей); org: после структурного фильтра
    // record/entity: записей, чей транскрипт вошёл целиком ИЛИ усечённо (усечение → truncated);
    // org: записей с окнами сегментов (≤12)
    recordsWithTranscript: z.number().int(),
    recordsCited: z.number().int(),
    truncated: z.boolean(), // сработал потолок: 5 000 id (org) / 150k символов (entity) / 100k символов записи
    // org: найденных записей, текст которых отказала редакция ПДн (#2427); entity: записей
    // ленты, которые она отказала (#2473) — ответ построен без них и говорит об этом.
    // Нет в ответах, сохранённых до #2427, и когда отказов нет.
    recordsUnreadable: z.number().int().nonnegative().optional(),
  }),
  usage: z
    .object({
      model: z.string(),
      tokensIn: z.number().int(),
      tokensOut: z.number().int(),
    })
    .nullable(),
  quota: assistantQuotaSchema.nullable(), // null в истории треда; в ответе POST /ask и GET /quota — всегда
  errorCode: z.string().nullable(), // значения — assistantErrorCodeSchema; строка, чтобы новый код не ронял клиент
  // Вопрос хода, как он сохранён — после прохода ПДн (#2449): панель заменяет им набранный текст.
  // Только в ответах POST /ask и GET /turns/:id; в истории — turns[].question.
  question: z.string().optional(),
});

export const assistantThreadSchema = z.object({
  threadId: z.string().uuid(),
  scope: assistantScopeSchema,
  subject: assistantSubjectSchema, // для org — `{ scope:'org' }` без filters: фильтры — свойство хода, не треда
  turns: z.array(
    z.object({
      turnId: z.string().uuid(),
      question: z.string(), // редактированная версия — та же, что ушла в LLM (§3.1)
      // Ключ, под которым ход создан (§2.2): по нему клиент сверяет вопрос, чей
      // ответ потерян, — точно, а не «ход, которого не было до запроса»
      // (тот принимал ход другой вкладки). Листинг и так ограничен (org, user).
      idempotencyKey: z.string(),
      createdAt: z.string().datetime({ offset: true }),
      status: assistantTurnStatusSchema, // in-flight видны как «в работе» (§3.7)
      filters: assistantOrgFiltersSchema.nullable(), // из filters_json — без них «5 записей за август» нечитаемо
      answer: assistantAnswerSchema.nullable(), // usage/quota внутри — null
    })
  ),
});

// ============================================================================
// Type exports (inferred from schemas)
// ============================================================================

export type AssistantScope = z.infer<typeof assistantScopeSchema>;
export type AssistantTurnStatus = z.infer<typeof assistantTurnStatusSchema>;
export type AssistantErrorCode = z.infer<typeof assistantErrorCodeSchema>;
export type AssistantGrounding = z.infer<typeof assistantGroundingSchema>;
export type AssistantOrgFilters = z.infer<typeof assistantOrgFiltersSchema>;

/**
 * The one canonical text of a set of whole-base filters: equal filters, equal
 * key. The page compares a thread's filters with the ones it is about to send,
 * and the server decides which prior turns were asked «under the same
 * filters» — two copies of this rule would let them disagree about the same
 * pair. Key order, an absent field against `undefined`, an empty list against
 * an absent one, the order and repeats of an id list and the spelling of an
 * instant (`Z` or an offset) do not make two filters different; `null` is «no
 * filters» — the same as `{}`. An instant that does not parse keeps its text.
 *
 * Every field of `assistantOrgFiltersSchema` is listed by name: a field added
 * to the schema and forgotten here would be dropped from the comparison.
 */
export function assistantOrgFiltersKey(
  filters: AssistantOrgFilters | null | undefined
): string {
  if (!filters) return '{}';
  const instant = (value: string | undefined) => {
    if (!value) return undefined;
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? value : ms;
  };
  const ids = (values: readonly string[] | undefined) =>
    values && values.length > 0 ? [...new Set(values)].sort() : undefined;
  return JSON.stringify({
    dateFrom: instant(filters.dateFrom),
    dateTo: instant(filters.dateTo),
    projectIds: ids(filters.projectIds),
    agentIds: ids(filters.agentIds),
    type: filters.type,
    callCategory: filters.callCategory,
    entityName: filters.entityName,
  });
}
export type AssistantSubject = z.infer<typeof assistantSubjectSchema>;
export type AssistantAskRequest = z.infer<typeof assistantAskRequestSchema>;
export type AssistantThreadsQuery = z.infer<typeof assistantThreadsQuerySchema>;
export type AssistantEntityNamesQuery = z.infer<typeof assistantEntityNamesQuerySchema>;
export type AssistantEntityNamesFilters = z.infer<
  typeof assistantEntityNamesFiltersSchema
>;
export type AssistantEntityNamesResponse = z.infer<
  typeof assistantEntityNamesResponseSchema
>;
export type AssistantModelOutput = z.infer<typeof assistantModelOutputSchema>;
export type AssistantQuota = z.infer<typeof assistantQuotaSchema>;
export type AssistantQuotaResponse = z.infer<typeof assistantQuotaResponseSchema>;
export type AssistantCitation = z.infer<typeof assistantCitationSchema>;
export type AssistantPoint = z.infer<typeof assistantPointSchema>;
export type AssistantAnswer = z.infer<typeof assistantAnswerSchema>;
export type AssistantThread = z.infer<typeof assistantThreadSchema>;

/**
 * The thread key of a subject — `assistant_turns.subject_key`, the thread
 * lookup and the 409 check on the server, the query and panel keys on the
 * client: one function, so two different subjects can never share a key.
 * `entityId`/`installationId` are free text, so each is length-prefixed — a
 * `:` inside one cannot collide with the separator; `recordId` is a UUID.
 * Takes the shape the key needs, so a stored row qualifies as well as a subject.
 */
export type AssistantSubjectKeyInput =
  | { scope: 'record'; recordId: string }
  | {
      scope: 'entity';
      entityType: string;
      entityId: string;
      installationId: string | null;
    }
  | { scope: 'org' };

export function buildSubjectKey(subject: AssistantSubjectKeyInput): string {
  switch (subject.scope) {
    case 'record':
      return `record:${subject.recordId}`;
    case 'entity': {
      const id = `${subject.entityId.length}:${subject.entityId}`;
      const install =
        subject.installationId === null
          ? ''
          : `${subject.installationId.length}:${subject.installationId}`;
      return `entity:${subject.entityType}:${id}:${install}`;
    }
    case 'org':
      return 'org';
  }
}
